import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Box
} from '@mui/material';
import { Grid } from '@mui/material';
import { EmojiEvents, Speed, Map as MapIcon } from '@mui/icons-material';
import { listUrbanTracks, getUrbanTrackById } from '../game/tracks/UrbanTrackCatalog';
import { CAR_CATALOG, CAR_STARTER_ID } from '../game/cars/CarCatalog';

interface RaceSetupDialogProps {
  open: boolean;
  onStart: (carId: string, trackId: string) => void;
  onCancel: () => void;
  onOpenGarage?: () => void;
}

export const RaceSetupDialog: React.FC<RaceSetupDialogProps> = ({ 
  open, 
  onStart, 
  onCancel,
  onOpenGarage,
}) => {
  const [selectedCar, setSelectedCar] = useState<string>(CAR_STARTER_ID);
  const [selectedTrack, setSelectedTrack] = useState<string>('city_circuit_night');
  const tracks = listUrbanTracks();
  const cars = CAR_CATALOG;

  const handleStart = () => {
    onStart(selectedCar, selectedTrack);
  };

  const getDifficultyColor = (difficulty: number): 'success' | 'warning' | 'error' => {
    if (difficulty <= 2) return 'success';
    if (difficulty <= 3) return 'warning';
    return 'error';
  };
  
  const getDifficultyLabel = (difficulty: number): string => {
    const labels = ['n/a', 'Leicht', 'Mittel', 'Schwer', 'Sehr Schwer', 'Extrem'];
    return labels[difficulty] || 'Mittel';
  };

  const currentTrack = getUrbanTrackById(selectedTrack);
  const currentCar = cars.find(car => car.id === selectedCar) || cars[0];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <EmojiEvents />
        Rennveranstaltung starten
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Auto-Auswahl */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Speed /> Auto auswählen
            </Typography>
            <Grid container spacing={2}>
              {cars.map((car) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={car.id}>
                  <Card
                    onClick={() => setSelectedCar(car.id)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedCar === car.id ? '3px solid #2196f3' : '1px solid #ddd',
                      transition: 'all 0.3s',
                      '&:hover': { boxShadow: 3, transform: 'scale(1.02)' }
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {car.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip
                          label={`Kraft: ${car.accel.toFixed(1)}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Grip: ${car.grip.toFixed(1)}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        Max: {car.maxSpeed.toFixed(0)} km/h
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Strecken-Auswahl */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <MapIcon /> Strecke auswählen
            </Typography>
            <Grid container spacing={2}>
              {tracks.map((track) => (
                <Grid size={{ xs: 12, sm: 6, md: 6 }} key={track.id}>
                  <Card
                    onClick={() => setSelectedTrack(track.id)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedTrack === track.id ? '3px solid #4caf50' : '1px solid #ddd',
                      transition: 'all 0.3s',
                      '&:hover': { boxShadow: 3, transform: 'scale(1.02)' }
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {track.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {track.description}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Chip
                          label={`${track.length.toFixed(2)} km`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={getDifficultyLabel(track.difficulty)}
                          size="small"
                          color={getDifficultyColor(track.difficulty)}
                        />
                        {track.raceType === 'circuit' && track.laps && (
                          <Chip
                            label={`${track.laps} Runden`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {track.raceType === 'sprint' && (
                          <Chip
                            label="Sprint (A→B)"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Zusammenfassung */}
          {currentCar && currentTrack && (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                📋 Rennübersicht
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Auto: <strong>{currentCar.name}</strong> | Strecke: <strong>{currentTrack.name}</strong>
              </Typography>
              <Typography variant="caption">
                Länge: {currentTrack.length.toFixed(2)} km | Schwierigkeit: {getDifficultyLabel(currentTrack.difficulty)}
                {currentTrack.raceType === 'circuit' && currentTrack.laps && ` | ${currentTrack.laps} Runden`}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <Box>
          <Button onClick={onCancel} variant="outlined">
            Abbrechen
          </Button>
          {onOpenGarage && (
            <Button 
              onClick={onOpenGarage} 
              variant="outlined"
              sx={{ ml: 1 }}
            >
              🔧 Garage
            </Button>
          )}
        </Box>
        <Button
          onClick={handleStart}
          variant="contained"
          color="success"
          size="large"
          sx={{ fontWeight: 'bold' }}
        >
          🏁 Rennen starten!
        </Button>
      </DialogActions>
    </Dialog>
  );
};
